// Polyfills for Chrome 61 compatibility
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-this-alias */
import 'core-js/stable';
import 'regenerator-runtime/runtime';

// globalThis polyfill
if (typeof globalThis === 'undefined') {
  (function () {
    if (typeof self !== 'undefined') {
      // @ts-expect-error -- globalThis polyfill for legacy browsers
      self.globalThis = self;
    } else if (typeof window !== 'undefined') {
      // @ts-expect-error -- globalThis polyfill for legacy browsers
      window.globalThis = window;
    } else if (typeof global !== 'undefined') {
      // @ts-expect-error -- globalThis polyfill for legacy browsers
      global.globalThis = global;
    } else {
      throw new Error('Unable to locate global object');
    }
  })();
}

// Object.hasOwn polyfill
// @ts-expect-error -- Object.hasOwn not available in older TS targets
if (!Object.hasOwn) {
  // @ts-expect-error -- polyfill assignment
  Object.hasOwn = function (obj: any, prop: string | symbol) {
    return Object.prototype.hasOwnProperty.call(obj, prop);
  };
}

// Object.assign polyfill (for older browsers)
if (!Object.assign) {
  Object.assign = function (target: any, ...sources: any[]) {
    if (target == null) {
      throw new TypeError('Cannot convert undefined or null to object');
    }
    const to = Object(target);
    for (const source of sources) {
      if (source != null) {
        for (const key in source) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            to[key] = source[key];
          }
        }
      }
    }
    return to;
  };
}

// Array.from polyfill
if (!Array.from) {
  Array.from = function (arrayLike: any, mapFn?: any, thisArg?: any) {
    const C = this;
    const items = Object(arrayLike);
    if (arrayLike == null) {
      throw new TypeError(
        'Array.from requires an array-like object - not null or undefined',
      );
    }
    const len = Number(items.length) || 0;
    const A = typeof C === 'function' ? Object(new C(len)) : new Array(len);
    let k = 0;
    while (k < len) {
      const kValue = items[k];
      if (mapFn) {
        A[k] = thisArg ? mapFn.call(thisArg, kValue, k) : mapFn(kValue, k);
      } else {
        A[k] = kValue;
      }
      k += 1;
    }
    A.length = len;
    return A;
  };
}

// Array.includes polyfill
if (!Array.prototype.includes) {
  Array.prototype.includes = function (searchElement: any, fromIndex?: number) {
    const O = Object(this);
    const len = parseInt(O.length) || 0;
    if (len === 0) return false;
    const n = fromIndex || 0;
    let k = Math.max(n >= 0 ? n : len - Math.abs(n), 0);
    while (k < len) {
      if (searchElement === O[k]) return true;
      k++;
    }
    return false;
  };
}

// Optional chaining polyfill is handled by Babel/Vite plugin-legacy
// Nullish coalescing polyfill is handled by Babel/Vite plugin-legacy

// Object.fromEntries polyfill
if (!Object.fromEntries) {
  Object.fromEntries = function <T = any>(
    entries: Iterable<readonly [PropertyKey, T]>,
  ): { [k: string]: T } {
    const obj: { [k: string]: T } = {};
    for (const [key, value] of entries) {
      obj[String(key)] = value;
    }
    return obj;
  };
}

// String.prototype.matchAll polyfill (basic implementation)
if (!String.prototype.matchAll) {
  String.prototype.matchAll = function (regexp: RegExp) {
    const matches = [];
    const str = this.toString();
    let match;

    if (!regexp.global) {
      throw new TypeError(
        'String.prototype.matchAll called with a non-global RegExp',
      );
    }

    const regex = new RegExp(regexp);
    while ((match = regex.exec(str)) !== null) {
      matches.push(match);
    }

    return matches[Symbol.iterator]();
  };
}

// Promise.allSettled polyfill
if (!Promise.allSettled) {
  Promise.allSettled = function (
    promises: any[],
  ): Promise<PromiseSettledResult<any>[]> {
    return Promise.all(
      promises.map((p: any) =>
        Promise.resolve(p).then(
          (value: any) => ({ status: 'fulfilled' as const, value }),
          (reason: any) => ({ status: 'rejected' as const, reason }),
        ),
      ),
    );
  } as any;
}

// Array.prototype.flat polyfill
if (!Array.prototype.flat) {
  Object.defineProperty(Array.prototype, 'flat', {
    value: function (depth = 1) {
      return depth > 0
        ? this.reduce(
            (acc: any[], val: any) =>
              acc.concat(Array.isArray(val) ? val.flat(depth - 1) : val),
            [],
          )
        : this.slice();
    },
  });
}

// Array.prototype.flatMap polyfill
if (!Array.prototype.flatMap) {
  Object.defineProperty(Array.prototype, 'flatMap', {
    value: function (callback: any, thisArg?: any) {
      return this.map(callback, thisArg).flat();
    },
  });
}

// Basic Proxy polyfill check (Chrome 61 has Proxy but might be incomplete)
if (typeof Proxy === 'undefined' || typeof Reflect === 'undefined') {
  console.warn(
    'Proxy or Reflect not supported. Some React 19 features may not work correctly.',
  );
}

// Request Animation Frame polyfill
if (!window.requestAnimationFrame) {
  window.requestAnimationFrame = function (callback: FrameRequestCallback) {
    return window.setTimeout(callback, 1000 / 60);
  };
}

// Cancel Animation Frame polyfill
if (!window.cancelAnimationFrame) {
  window.cancelAnimationFrame = function (id: number) {
    clearTimeout(id);
  };
}

// Performance.now polyfill
if (!window.performance || !window.performance.now) {
  window.performance = window.performance || {};
  window.performance.now = function () {
    return Date.now();
  };
}

// CustomEvent polyfill
if (typeof window.CustomEvent !== 'function') {
  // @ts-expect-error -- CustomEvent polyfill for legacy browsers
  window.CustomEvent = function (event: string, params: any) {
    params = params || { bubbles: false, cancelable: false, detail: null };
    const evt = document.createEvent('CustomEvent');
    evt.initCustomEvent(
      event,
      params.bubbles,
      params.cancelable,
      params.detail,
    );
    return evt;
  };
}
